<script setup>
import { Head } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'

defineOptions({ layout: PublicLayout })

const topics = [
  { title: 'Visa & entry', body: 'Most visitors need an eVisa — apply online before you travel (the East Africa Tourist Visa also covers Kenya, Uganda and Rwanda). You’ll need a passport valid for at least six months and onward travel details.' },
  { title: 'Getting there', body: 'The main gateway is Jomo Kenyatta International (NBO) in Nairobi; Moi International (MBA) in Mombasa serves the coast directly. Frequent connections from Europe, the Gulf and across Africa.' },
  { title: 'Getting around', body: 'Domestic flights link Nairobi, the Mara, the coast and beyond quickly. On the ground, your experiences include transport with a guide. In Nairobi, ride-hailing apps are easy and cheap.' },
  { title: 'Money & M-Pesa', body: 'The currency is the Kenyan Shilling (KES); we show prices in USD too. Cards work in cities and lodges, but cash matters in markets — and almost everything runs on M-Pesa mobile money, which is worth setting up if you stay a while.' },
  { title: 'Health & safety', body: 'Check current vaccination advice (yellow fever may be required depending on your route) and consider anti-malarials for safari and coast. Bring sunblock, a hat and any personal medication. Travel insurance is strongly recommended.' },
  { title: 'What to pack', body: 'Neutral colours for safari, layers for cold dawns and hot middays, swimwear for the coast, a good camera, binoculars and far less than you think. Laundry is easy; over-packing isn’t worth it.' },
]
</script>

<template>
  <Head>
    <title>Plan your trip · Kenya2Go</title>
    <meta name="description" content="The practical essentials for a trip to Kenya — visas, flights, money & M-Pesa, health, and what to pack.">
  </Head>

  <div class="kp">
    <header class="kp-hero">
      <div class="kp-hero__inner">
        <p class="kp-eyebrow">Plan your trip</p>
        <h1 class="kp-hero__title">The practical bits</h1>
        <p class="kp-hero__lede">The essentials for a smooth Kenya trip. Anything not covered here, just ask — a real person is happy to help.</p>
      </div>
    </header>

    <section class="kp-section">
      <div class="pgrid">
        <article v-for="(t, i) in topics" :key="t.title" class="pcard">
          <span class="pcard__n">{{ String(i + 1).padStart(2, '0') }}</span>
          <h2 class="pcard__title">{{ t.title }}</h2>
          <p class="pcard__body">{{ t.body }}</p>
        </article>
      </div>

      <div class="plinks">
        <a href="/seasonal" class="plink">
          <h3>When to go →</h3>
          <p>The Migration, dry and green seasons — month by month.</p>
        </a>
        <a href="/contact" class="plink plink--accent">
          <h3>Still have questions? →</h3>
          <p>Talk to a real person about your trip.</p>
        </a>
      </div>
    </section>
  </div>
</template>

<style scoped>
.pgrid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.4rem; }
.pcard { padding: 1.6rem 1.7rem; border-radius: var(--radius-lg); background: var(--surface); border: 1px solid var(--line); }
.pcard__n { font-family: var(--mono); font-size: 0.8rem; color: var(--accent); font-weight: 600; }
.pcard__title { font-family: var(--display); font-weight: 500; font-size: 1.35rem; margin: 0.3rem 0 0.6rem; color: var(--ink); }
.pcard__body { font-size: 0.96rem; line-height: 1.65; color: var(--inkSoft); margin: 0; }

.plinks { display: grid; grid-template-columns: 1fr 1fr; gap: 1.2rem; margin-top: 1.6rem; }
.plink { display: block; padding: 1.4rem 1.6rem; border-radius: var(--radius-lg); border: 1px solid var(--line); background: var(--surface); text-decoration: none; transition: border-color .15s ease, transform .15s ease; }
.plink:hover { border-color: var(--accent); transform: translateY(-2px); }
.plink h3 { font-family: var(--display); font-weight: 500; font-size: 1.15rem; margin: 0 0 0.3rem; color: var(--ink); }
.plink p { font-size: 0.9rem; color: var(--inkSoft); margin: 0; }
.plink--accent { background: var(--accentSoft); border-color: transparent; }

@media (max-width: 640px) { .pgrid, .plinks { grid-template-columns: 1fr; } }
</style>
